/**
 * Classifier tab — model + system prompt + editable staff roster.
 *
 * Reads/writes GET|PUT /api/admin/classifier-config. The roster is the list of
 * staff first names the classifier uses to attribute a call to a rep; it's a
 * chip list (add via input, remove via the chip's ✕). Blank system prompt falls
 * back to the server's built-in CLASSIFY_SYSTEM_PROMPT.
 */
import { useEffect, useState, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { ModelCard, SystemPromptCard, SavedNote, ErrorNote } from "./shared";

const CLASSIFIER_KEY = ["admin", "classifier-config"] as const;

export function ClassifierTab() {
  const qc = useQueryClient();

  const configQuery = useQuery({
    queryKey: CLASSIFIER_KEY,
    queryFn: ({ signal }) => api.adminGetClassifierConfig(signal),
  });

  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [names, setNames] = useState<string[]>([]);
  const [draftName, setDraftName] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  const data = configQuery.data;
  useEffect(() => {
    if (data) {
      setModel(data.model);
      setSystemPrompt(data.system_prompt);
      setNames(data.staff_first_names);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: {
      model?: string;
      system_prompt?: string;
      staff_first_names?: string[];
    }) => api.adminSaveClassifierConfig(body),
    onSuccess: () => {
      // PUT returns {ok,changed}; the GET refetch supplies the authoritative config.
      void qc.invalidateQueries({ queryKey: CLASSIFIER_KEY });
      setJustSaved(true);
    },
  });

  if (configQuery.isLoading) {
    return <LoadingState label="Loading classifier settings…" />;
  }
  if (configQuery.isError || !data) {
    return (
      <ErrorState
        error={configQuery.error}
        onRetry={() => void configQuery.refetch()}
      />
    );
  }

  const isCustom = data.system_prompt_is_custom;
  const namesDirty =
    names.length !== data.staff_first_names.length ||
    names.some((n, i) => n !== data.staff_first_names[i]);
  const dirty =
    model !== data.model ||
    systemPrompt !== data.system_prompt ||
    namesDirty;
  const saving = save.isPending;

  function addName(raw: string) {
    const name = raw.trim();
    if (!name) return;
    if (names.some((n) => n.toLowerCase() === name.toLowerCase())) {
      setDraftName("");
      return;
    }
    setJustSaved(false);
    setNames((prev) => [...prev, name]);
    setDraftName("");
  }
  function removeName(i: number) {
    setJustSaved(false);
    setNames((prev) => prev.filter((_, idx) => idx !== i));
  }
  function onDraftKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addName(draftName);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Call-type classifier</h2>
          <p className="text-sm text-muted-foreground">
            Decides each call&apos;s type and which rep handled it before grading.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button
            onClick={() => {
              setJustSaved(false);
              save.mutate({
                model,
                system_prompt: systemPrompt,
                staff_first_names: names,
              });
            }}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <ModelCard
        label="Classifier model"
        help="The model Claude uses to classify the call type and attribute the rep."
        options={data.model_options}
        value={model}
        onChange={(v) => {
          setJustSaved(false);
          setModel(v);
        }}
      />

      <SystemPromptCard
        title="Classifier system prompt"
        value={systemPrompt}
        onChange={(v) => {
          setJustSaved(false);
          setSystemPrompt(v);
        }}
        isCustom={isCustom}
        rows={16}
        onReset={() => {
          setJustSaved(false);
          setSystemPrompt("");
          save.mutate({ system_prompt: "" });
        }}
        resetDisabled={saving || !isCustom}
        help="Leave blank to use the built-in default classifier prompt."
        errorSlot={<ErrorNote show={save.isError} />}
      />

      <Card className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Staff roster</h2>
        <p className="text-sm text-muted-foreground">
          First names the classifier matches against to attribute a call to a
          rep. Type a name and press Enter (or comma) to add.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {names.map((n, i) => (
            <span
              key={`${n}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-sm"
            >
              {n}
              <button
                type="button"
                title={`Remove ${n}`}
                onClick={() => removeName(i)}
                className="rounded-full text-muted-foreground transition hover:text-foreground"
              >
                ✕
              </button>
            </span>
          ))}
          {names.length === 0 && (
            <span className="text-sm text-muted-foreground">
              No names yet.
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={onDraftKeyDown}
            placeholder="Add a first name…"
            className="max-w-xs rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button
            variant="ghost"
            className="text-xs"
            onClick={() => addName(draftName)}
            disabled={!draftName.trim()}
          >
            Add
          </Button>
        </div>
      </Card>
    </div>
  );
}
